import { I18n } from '@/i18n/I18n';
import { ChevronUp, ChevronDown, Copy, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { ConfirmModal } from '../modals/ConfirmModal';

export function ProductPageEditHeader<T extends { id: string, position: number }>(props: {
  children: React.ReactNode,
  siblings: T[],
  item: T,
  reorder: (items: T[]) => Promise<void>,
  duplicate: (item: T) => Promise<void>,
  deleteAction: (item: T) => Promise<unknown>
}) {
  const { siblings, item, children, reorder, duplicate, deleteAction } = props;
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const contentIndex = siblings.findIndex(c => c.id === item.id);
  const isFirst = contentIndex === 0;
  const isLast = contentIndex === siblings.length - 1;

  const handleMoveUp = () => {
    if (isFirst) return;
    const prev = siblings[contentIndex - 1];
    swapPositions(item, prev);
  };

  const handleMoveDown = () => {
    if (isLast) return;
    const next = siblings[contentIndex + 1];
    swapPositions(item, next);
  };

  const swapPositions = (a: T, b: T) => {
    const updated = siblings.map(c => {
      if (c.id === a.id) return { ...c, position: b.position };
      if (c.id === b.id) return { ...c, position: a.position };
      return c;
    });
    void reorder(updated);
  };

  return (
    <div className="d-flex justify-content-between align-items-center">
    {children}
    <div className="d-flex gap-1">
      <button
        type="button"
        className="fusa-btn-small-with-icon btn-neutral-light"
        onClick={handleMoveUp}
        disabled={isFirst}
        aria-label={I18n.get('moveUp')}
      >
        <ChevronUp size={16} />
      </button>
      <button
        type="button"
        className="fusa-btn-small-with-icon btn-neutral-light"
        onClick={handleMoveDown}
        disabled={isLast}
        aria-label={I18n.get('moveDown')}
      >
        <ChevronDown size={16} />
      </button>
    </div>
    <div className="d-flex gap-1">
      <button
        type="button"
        className="fusa-btn-small-with-icon btn-neutral-light"
        onClick={() => void duplicate(item)}
        aria-label={I18n.get('duplicate')}
      >
        <Copy size={16} />
      </button>
      <button
        type="button"
        className="fusa-btn-small-with-icon btn-neutral-light"
        onClick={() => setShowDeleteModal(true)}
        aria-label={I18n.get('delete')}
      >
        <Trash2 size={16} />
      </button>
      {showDeleteModal && <ConfirmModal
        confirmMessage={I18n.get('areYouSureDelete')}
        onConfirm={() => deleteAction(item)}
        setShowModal={setShowDeleteModal}
        confirmText={I18n.get('delete')}
      />}
    </div>
  </div>
  );
}
